import { withNamespaces } from "react-i18next"
import Accordion from 'react-bootstrap/Accordion';
import { Col, Row, Table, UncontrolledTooltip } from "reactstrap";
import { useState } from "react";
import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';
import { RiFileChartLine, RiInformationLine } from "react-icons/ri";

const dateUtils = new DateUtils();

const formatValue = (value) => {
    if (!value?.unit || !value?.value) return '--';

    if (value.unit === '%') {
        return `${value.value.toFixed(4)}${value.unit}`
    }

    return (
        Intl.NumberFormat("en-US", {
            style: "currency",
            currency: value.unit,
            maximumFractionDigits: 2,
        }).format(Number(value.value))
    )
}

const groupSummary = (summary, t) => {
    const attributes = summary?.attributes;

    return [
        {
            "year": attributes.date,
            "sections": [
                {
                    "section": "Performance & Profitability",
                    "fields": [
                        {
                            "key": "revenue",
                            "title": "Revenue",
                            "value": formatValue(attributes.revenue),
                            "description": ""
                        },
                        {
                            "key": "earnings",
                            "title": "Earnings",
                            "value": formatValue(attributes.earnings),
                            "description": ""
                        },
                        {
                            "key": "returnOnSales",
                            "title": "Return on Sales",
                            "value": formatValue(attributes.returnOnSales),
                            "description": ""
                        },
                        {
                            "key": "returnOnEquity",
                            "title": "Return on Equity",
                            "value": formatValue(attributes.returnOnEquity),
                            "description": ""
                        },
                        {
                            "key": "revenueCAGR",
                            "title": "Revenue (CAGR)",
                            "value": formatValue(attributes.revenueCAGR),
                            "description": ""
                        },
                        {
                            "key": "earningsCAGR",
                            "title": "Earnings (CAGR)",
                            "value": formatValue(attributes.earningsCAGR),
                            "description": ""
                        }
                    ]
                },
                {
                    "section": "Capital Structure",
                    "fields": [
                        {
                            "key": "capital",
                            "title": "Capital",
                            "value": formatValue(attributes.capital),
                            "description": ""
                        },
                        {
                            "key": "equity",
                            "title": "Equity",
                            "value": formatValue(attributes.equity),
                            "description": ""
                        },
                        {
                            "key": "equityRatio",
                            "title": "Equity Ratio",
                            "value": formatValue(attributes.equityRatio),
                            "description": ""
                        }
                    ]
                },
                {
                    "section": "Balance Sheet Overview",
                    "fields": [
                        {
                            "key": "balanceTotal",
                            "title": "Balance Total",
                            "value": formatValue(attributes.balanceTotal),
                            "description": ""
                        },
                        {
                            "key": "liabilities",
                            "title": "Liabilities",
                            "value": formatValue(attributes.liabilities),
                            "description": ""
                        },
                        {
                            "key": "receivables",
                            "title": "Receivables",
                            "value": formatValue(attributes.receivables),
                            "description": ""
                        },
                        {
                            "key": "cash",
                            "title": "Cash",
                            "value": formatValue(attributes.cash),
                            "description": ""
                        },
                        {
                            "key": "realEstate",
                            "title": "Real Estate",
                            "value": formatValue(attributes.realEstate),
                            "description": ""
                        },
                        {
                            "key": "pensionProvisions",
                            "title": "Pension Provisions",
                            "value": formatValue(attributes.pensionProvisions),
                            "description": ""
                        }
                    ]
                },
                {
                    "section": "Operational Costs",
                    "fields": [
                        {
                            "key": "salaries",
                            "title": "Salaries",
                            "value": formatValue(attributes.salaries),
                            "description": "Total salaries paid to employees during the reporting period."
                        },
                        {
                            "key": "salaryPerEmployee",
                            "title": "Salary per Employee",
                            "value": formatValue(attributes.salaryPerEmployee),
                            "description": ""
                        },
                        {
                            "key": "materials",
                            "title": "Materials",
                            "value": formatValue(attributes.materials),
                            "description": ""
                        },
                        {
                            "key": "tax",
                            "title": "Tax",
                            "value": formatValue(attributes.tax),
                            "description": ""
                        },
                        {
                            "key": "taxRatio",
                            "title": "Tax Ratio",
                            "value": formatValue(attributes.taxRatio),
                            "description": ""
                        }
                    ]
                },
                {
                    "section": "Productivity Metrics",
                    "fields": [
                        {
                            "key": "revenuePerEmployee",
                            "title": "Revenue per Employee",
                            "value": formatValue(attributes.revenuePerEmployee),
                            "description": ""
                        }
                    ]
                }
            ]
        }
    ]
}

const FinancialSummary = ({
    t,
    activeAccordion,
    setActiveAccordion,

    detailsQuery
}) => {
    const summary = detailsQuery.data?.financial_summary?.rawData;

    const [showMore, setShowMore] = useState(false);

    const financialData = groupSummary(summary, t)

    return (
        <Accordion.Item
            onClick={() => setActiveAccordion('financial-summary')}
            className={activeAccordion === 'financial-summary' ? 'active' : ''}
            eventKey={'financial-summary'}
        >
            <Accordion.Header>
                <RiFileChartLine size={24} className="me-2" />
                <span>
                    {t('Financial Summary')}
                </span>
            </Accordion.Header>

            <Accordion.Body>
                <Row>
                    <Col sm="12">
                        <div
                            id="partner-financial-content"
                            style={{
                                overflow: showMore ? 'visible' : 'hidden',
                                maxHeight: showMore ? '100%' : '600px'
                            }}
                        >
                            <Table striped responsive size="md" inverse>
                                <thead>
                                    <tr className="table-light">
                                        <th scope="col" style={{ width: '25%' }}>
                                            {`${t('Metric')} / ${t('Description')}`}
                                        </th>
                                        {financialData.map((yearData) => {
                                            return (
                                                <th scope="col" style={{
                                                    width: `${75 / financialData.length}%`
                                                }}>
                                                    {dateUtils.convertDateToDate(yearData.year, INTERNATIONAL_DATE_FORMAT)}
                                                </th>
                                            )
                                        })}
                                    </tr>
                                </thead>

                                {financialData[0].sections.map((section, sectionIndex) => {
                                    return (
                                        <tbody key={sectionIndex}>
                                            <tr>
                                                <th className="text-center" colSpan={financialData.length + 1}>
                                                    {t(section.section)}
                                                </th>
                                            </tr>
                                            {section.fields.map((field, fieldIndex) => {
                                                return (
                                                    <tr key={sectionIndex + fieldIndex}>
                                                        <th>
                                                            {t(field.title)}
                                                            {field.description ? (
                                                                <>
                                                                    <span
                                                                        id={`tooltip-${sectionIndex}-${fieldIndex}`}
                                                                        className="ms-1"
                                                                        style={{ cursor: "pointer" }}>
                                                                        <RiInformationLine />
                                                                    </span>
                                                                    <UncontrolledTooltip
                                                                        target={`tooltip-${sectionIndex}-${fieldIndex}`}
                                                                    >
                                                                        {t(field.description)}
                                                                    </UncontrolledTooltip>
                                                                </>
                                                            ) : null}
                                                        </th>
                                                        {financialData.map((eachFieldYear, yearIndex) => {
                                                            const val = eachFieldYear.sections[sectionIndex].fields[fieldIndex].value;

                                                            return (
                                                                <td key={sectionIndex + fieldIndex + yearIndex}>
                                                                    {val}
                                                                </td>
                                                            )
                                                        })}
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    )
                                })}
                            </Table>
                        </div>

                        <div
                            className="show-more text-center font-size-16 text-primary mt-2"
                            onClick={() => setShowMore(!showMore)}
                        >
                            <i className="ri-arrow-down-s-line font-size-22 align-middle" />
                            <span>
                                {t(showMore ? 'View less' : 'View more')}
                            </span>
                        </div>
                    </Col>
                </Row>
            </Accordion.Body>
        </Accordion.Item>
    )
}

export default withNamespaces()(
    FinancialSummary
)